import React, { ReactNode, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, useContext } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import PipelineMainBg from '../assets/pipeline-main-bg.jpg';
import PipelineSubBg from '../assets/pipeline-sub-bg.jpg';
import { DeckRenderMode } from '../types';

export const CANVAS_W = 1600;
export const CANVAS_H = 900;

export const DECK_NAVY = '#123b78';
export const DECK_NAVY_DEEP = '#0d2c5c';

const MIN_PREVIEW_ZOOM = 0.75;
const MAX_PREVIEW_ZOOM = 3;
const PREVIEW_ZOOM_STEP = 0.1;
const previewZoomByPage = new Map<string, number>();

type DeckAutoPanApi = {
  start: (clientX: number, clientY: number) => void;
  move: (clientX: number, clientY: number) => void;
  stop: () => void;
};

const noopAutoPan: DeckAutoPanApi = { start: () => undefined, move: () => undefined, stop: () => undefined };
const DeckAutoPanContext = React.createContext<DeckAutoPanApi>(noopAutoPan);

export const useDeckAutoPan = (): DeckAutoPanApi => useContext(DeckAutoPanContext);

const DISCLAIMER =
  'Source: Huttons Analytics, URA, HDB & JTC. All units are estimated numbers only. Map is not drawn to scale. ' +
  'All images and other graphic representations are artist’s impression only and may be subject to changes. ' +
  'While we have endeavoured to ensure that the information and materials contained herein are accurate and up to date, ' +
  'we are not responsible for any errors or omissions, or for the results obtained\nfrom their use or the reliance placed on them. ' +
  'All information is provided "as is", with no guarantee of completeness, and accuracy. In no event will Huttons and/or' +
  'salespersons thereof be liable in contract or in tort, to any party for any decision made or action taken in reliance on the ' +
  'information in this presentation/document or for any direct, indirect, consequential, special or similar damages. Seeking Indication of Interest.';

interface DeckPageProps {
  children: ReactNode;
  /** Fixed-size render for PDF capture (no scale-to-fit wrapper). */
  fixed?: boolean;
  /** data attribute used by the export capture. */
  pageKey: string;
  /**
   * Brand background artwork. 'main' (cover/overview) uses the main branded
   * artwork; 'sub' uses the downstream branded artwork.
   */
  background?: 'main' | 'sub';
  /** Optional user-uploaded background image (data URL). */
  backgroundImage?: string;
  /** Optional typography for downstream pipeline tabs. */
  fontFamily?: string;
  renderMode?: DeckRenderMode;
}

/**
 * 16:9 deck page canvas (1600×900 logical px). In preview mode the canvas
 * scales to fit its container width while children keep absolute px
 * coordinates; in fixed mode it renders at natural size for html2canvas.
 */
export const DeckPage: React.FC<DeckPageProps> = ({ children, fixed = false, pageKey, background = 'sub', backgroundImage, fontFamily, renderMode = 'interactive' }) => {
  const hostRef = useRef<HTMLDivElement | null>(null);
  const [scale, setScale] = useState(1);
  const [zoom, setZoom] = useState(() => previewZoomByPage.get(pageKey) ?? 1);
  const hoveredRef = useRef(false);
  const autoPanState = useRef<{ active: boolean; pointerX: number; pointerY: number; frame: number | null }>({ active: false, pointerX: 0, pointerY: 0, frame: null });

  useEffect(() => {
    setZoom(previewZoomByPage.get(pageKey) ?? 1);
  }, [pageKey]);

  const updateZoom = (next: number) => {
    const normalized = Math.max(MIN_PREVIEW_ZOOM, Math.min(MAX_PREVIEW_ZOOM, Math.round(next * 10) / 10));
    previewZoomByPage.set(pageKey, normalized);
    setZoom(normalized);
  };

  useEffect(() => {
    if (fixed) return;
    const isTextTarget = (target: EventTarget | null) => {
      const element = target as HTMLElement | null;
      return !!element && (element.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT', 'BUTTON'].includes(element.tagName));
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (!(event.ctrlKey || event.metaKey) || isTextTarget(event.target)) return;
      const host = hostRef.current;
      const activeInDeck = !!host && (host.contains(document.activeElement) || hoveredRef.current);
      if (!activeInDeck) return;
      const key = event.key;
      if (key === '+' || key === '=') {
        event.preventDefault();
        updateZoom(zoom + PREVIEW_ZOOM_STEP);
      } else if (key === '-' || key === '_') {
        event.preventDefault();
        updateZoom(zoom - PREVIEW_ZOOM_STEP);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [fixed, zoom, pageKey]);

  useEffect(() => {
    if (fixed) return;
    const host = hostRef.current;
    if (!host) return;
    const update = () => {
      // Measure the stable layout wrapper instead of the scroll host. The
      // scroll host's clientWidth can change when a scrollbar appears at
      // fractional browser zoom levels, which otherwise creates a resize loop.
      const outerWidth = host.parentElement?.getBoundingClientRect().width ?? host.getBoundingClientRect().width;
      const nextScale = Math.round((outerWidth / CANVAS_W) * 1_000_000) / 1_000_000;
      setScale((current) => Math.abs(current - nextScale) < 0.000001 ? current : nextScale);
    };
    update();
    const observer = new ResizeObserver(update);
    if (host.parentElement) observer.observe(host.parentElement);
    return () => observer.disconnect();
  }, [fixed]);

  const stopAutoPan = useCallback(() => {
    const state = autoPanState.current;
    state.active = false;
    if (state.frame != null) window.cancelAnimationFrame(state.frame);
    state.frame = null;
  }, []);

  const moveAutoPan = useCallback((clientX: number, clientY: number) => {
    if (fixed) return;
    const state = autoPanState.current;
    state.pointerX = clientX;
    state.pointerY = clientY;
    if (state.frame != null) return;
    const tick = () => {
      state.frame = null;
      if (!state.active) return;
      const host = hostRef.current;
      if (!host) return;
      const rect = host.getBoundingClientRect();
      const threshold = 64;
      const speed = (distance: number) => Math.max(0, Math.min(24, (threshold - distance) * 0.38));
      const left = state.pointerX - rect.left;
      const right = rect.right - state.pointerX;
      const top = state.pointerY - rect.top;
      const bottom = rect.bottom - state.pointerY;
      const dx = left < threshold ? -speed(left) : right < threshold ? speed(right) : 0;
      const dy = top < threshold ? -speed(top) : bottom < threshold ? speed(bottom) : 0;
      if (dx || dy) {
        host.scrollLeft += dx;
        host.scrollTop += dy;
        state.frame = window.requestAnimationFrame(tick);
      }
    };
    state.frame = window.requestAnimationFrame(tick);
  }, [fixed]);

  const startAutoPan = useCallback((clientX: number, clientY: number) => {
    if (fixed) return;
    const state = autoPanState.current;
    state.active = true;
    moveAutoPan(clientX, clientY);
  }, [fixed, moveAutoPan]);

  const autoPan = useMemo(() => ({ start: startAutoPan, move: moveAutoPan, stop: stopAutoPan }), [moveAutoPan, startAutoPan, stopAutoPan]);
  const resolvedBackgroundImage = backgroundImage || (background === 'main' ? PipelineMainBg : PipelineSubBg);

  const canvas = (
    <Box
      data-pipeline-page={pageKey}
      data-deck-render-mode={renderMode}
      sx={{
        width: CANVAS_W,
        height: CANVAS_H,
        position: 'relative',
        overflow: 'hidden',
        backgroundColor: DECK_NAVY,
        backgroundImage: `url(${resolvedBackgroundImage})`,
        backgroundSize: '100% 100%',
        backgroundRepeat: 'no-repeat',
        fontFamily: fontFamily || "'Liberation Sans', sans-serif",
        fontSynthesis: 'none',
        flexShrink: 0,
        ...(renderMode === 'print' ? { animation: 'none', transition: 'none' } : {}),
        ...(fixed ? {} : { transform: `scale(${scale * zoom})`, transformOrigin: 'top left' }),
      }}
    >
      {renderMode === 'print' && (
        <Box
          component="img"
          src={resolvedBackgroundImage}
          crossOrigin="anonymous"
          alt=""
          aria-hidden="true"
          data-deck-background="true"
          sx={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
            objectFit: 'fill',
            pointerEvents: 'none',
            userSelect: 'none',
            zIndex: 0,
          }}
        />
      )}
      {children}
    </Box>
  );

  if (fixed) return canvas;

  return (
    <Box
      sx={{
        width: '100%', position: 'relative', overflow: 'visible',
      }}
      onPointerEnter={() => { hoveredRef.current = true; }}
      onPointerLeave={() => { hoveredRef.current = false; }}
    >
      <DeckAutoPanContext.Provider value={autoPan}>
      <Box
        sx={{ position: 'absolute', top: -42, right: 0, zIndex: 30, display: 'flex', alignItems: 'center', gap: 0.5 }}
      >
        <Button
          size="small"
          variant="outlined"
          onClick={() => updateZoom(zoom - PREVIEW_ZOOM_STEP)}
          disabled={zoom <= MIN_PREVIEW_ZOOM}
          aria-label="Zoom out"
          sx={{ minWidth: 32, px: 1, color: 'var(--color-text-strong)', borderColor: 'var(--color-border)' }}
        >−</Button>
        <Button
          size="small"
          variant="outlined"
          onClick={() => updateZoom(zoom + PREVIEW_ZOOM_STEP)}
          disabled={zoom >= MAX_PREVIEW_ZOOM}
          aria-label="Zoom in"
          sx={{ minWidth: 32, px: 1, color: 'var(--color-text-strong)', borderColor: 'var(--color-border)' }}
        >+</Button>
        <Button
          size="small"
          variant="outlined"
          onClick={() => updateZoom(1)}
          aria-label="Reset zoom"
          sx={{ minWidth: 0, px: 1, color: 'var(--color-text-strong)', borderColor: 'var(--color-border)' }}
        >Reset zoom</Button>
      </Box>
      <Box
        ref={hostRef}
        data-deck-scroll
        sx={{
          width: '100%', aspectRatio: `${CANVAS_W} / ${CANVAS_H}`,
          borderRadius: 1.5, overflow: 'auto', scrollbarGutter: 'stable',
          boxShadow: '0 6px 24px rgba(var(--color-shadow-rgb), 0.35)',
        }}
      >
        <Box sx={{ width: CANVAS_W * scale * zoom, height: CANVAS_H * scale * zoom, minWidth: '100%', minHeight: '100%' }}>
          {React.cloneElement(canvas, { sx: { ...canvas.props.sx, willChange: 'transform' } })}
        </Box>
      </Box>
      </DeckAutoPanContext.Provider>
    </Box>
  );
};

interface DeckHeaderProps {
  /** e.g. "Residential" (italic grey sub-heading) */
  heading?: string | null;
  /** e.g. "NORTH & NORTH—EAST" coloured accent heading */
  accent?: string | null;
  accentColor?: string;
  compact?: boolean;
}

/** Standard page header: big deck title (the Huttons logo is baked into the background artwork). */
export const DeckHeader: React.FC<DeckHeaderProps> = ({
  heading,
  accent,
  accentColor = '#f5d76e',
  compact = false,
}) => {
  const [titleFontSize, setTitleFontSize] = useState(compact ? 41 : 52);
  useLayoutEffect(() => {
    const baseSize = compact ? 41 : 52;
    const availableWidth = CANVAS_W - 120;
    const measureCanvas = document.createElement('canvas');
    const context = measureCanvas.getContext('2d');
    if (!context) return;
    context.font = `700 ${baseSize}px "Liberation Sans"`;
    const text = 'PROJECTS IN THE PIPELINE';
    const letterSpacing = 1.5 * Math.max(0, text.length - 1);
    const measuredWidth = context.measureText(text).width + letterSpacing;
    const fitted = Math.min(baseSize, Math.max(32, baseSize * availableWidth / Math.max(1, measuredWidth)));
    setTitleFontSize((current) => Math.abs(current - fitted) < 0.1 ? current : fitted);
  }, [compact]);
  return (
  <>
    <Box
      sx={{
        position: 'absolute', top: compact ? 12 : 18, left: 60, right: 60,
        width: 'auto', textAlign: 'center', pointerEvents: 'none', minWidth: 0,
      }}
    >
      <Box
        data-deck-title="true"
        sx={{
          fontSize: titleFontSize,
          fontWeight: 700,
          color: '#ffffff',
          letterSpacing: 1.5,
          textShadow: '0 2px 6px rgba(0,0,0,0.45), 0 1px 0 rgba(255,255,255,0.25)',
          lineHeight: 1.05,
          whiteSpace: 'normal',
          overflow: 'visible',
          overflowWrap: 'normal',
          boxSizing: 'border-box',
        }}
      >
        PROJECTS IN THE PIPELINE
      </Box>
      {heading && (
        <Box sx={{ fontSize: compact ? 27 : 34, fontStyle: 'italic', color: '#c9d4e8', fontWeight: 600, mt: 0.25, whiteSpace: 'normal', overflow: 'visible', overflowWrap: 'anywhere' }}>
          {heading}
        </Box>
      )}
      {accent && (
        <Box sx={{ fontSize: compact ? 31 : 32, fontWeight: 700, color: accentColor, letterSpacing: 2, mt: 0.25, whiteSpace: 'normal', overflow: 'visible', overflowWrap: 'anywhere' }}>
          {accent}
        </Box>
      )}
    </Box>
  </>
  );
};

interface DeckFooterProps {
  asAtDate: string;
  /** Distance from the bottom edge of the deck canvas. */
  bottom?: number;
  /** Compact downstream export/slide treatment. */
  compact?: boolean;
}

/** Tiny source/disclaimer line at the bottom of the deck canvas. */
export const DeckFooter: React.FC<DeckFooterProps> = ({ asAtDate, bottom = 36, compact = false }) => (
  <Box
    sx={{
      position: 'absolute',
      left: 85,
      right: 85,
      bottom,
      fontSize: 6.5,
      fontWeight: 700,
      lineHeight: compact ? 1.15 : 1.35,
      color: 'rgba(235,240,249,0.9)',
      textAlign: 'center',
      pointerEvents: 'none',
    }}
  >
    {(() => {
      const text = DISCLAIMER.replace('up to date,', `up to date as at ${asAtDate},`);
      const [first, ...rest] = text.split('obtained');
      return (
        <>
          {first}obtained
          <br />
          {rest.join('obtained').trim()}
        </>
      );
    })()}
  </Box>
);

/** Pricing/source caption placed immediately beneath the MRT legend. */
export const DeckLegendCaption: React.FC<{
  x: number;
  y: number;
  asAtDate: string;
  compact?: boolean;
}> = ({ x, y, asAtDate, compact = false }) => (
  <Box sx={{ position: 'absolute', left: x, top: y, color: '#ffffff', lineHeight: 1.25, pointerEvents: 'none' }}>
    <Box sx={{ fontSize: compact ? 7 : 11 }}>Estimated Average Launch Prices (S$)</Box>
    <Box sx={{ fontSize: compact ? 6.5 : 10 }}>
      Source: Newspaper articles, in-house estimates as of {asAtDate}
      <br />
      (Subject to change without notice)
    </Box>
  </Box>
);

/** "* : Project Name" + "Launching Soon!" key (right side of map pages). */
export const DeckKey: React.FC<{ x: number; y: number; showLaunchingSoon?: boolean; scale?: number; launchingSoonColor?: string; launchingSoonText?: string }> = ({
  x,
  y,
  showLaunchingSoon = true,
  scale = 1,
  launchingSoonColor = '#111111',
  launchingSoonText = ': Launching Soon!',
}) => (
  <Box
    sx={{
      position: 'absolute', left: x, top: y, pointerEvents: 'none',
      transform: `scale(${scale})`, transformOrigin: 'top left',
    }}
  >
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <Box sx={{ color: '#ffffff', fontSize: 16, fontWeight: 900 }}>*</Box>
      <Box sx={{ color: '#ffffff', fontSize: 15, fontWeight: 800 }}>= Project Name</Box>
    </Box>
    {showLaunchingSoon && (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.6 }}>
      <Box sx={{ width: 26, height: 12, backgroundColor: launchingSoonColor, border: '2px solid #111111', borderRadius: 0.5 }} />
      <Box sx={{ color: '#ffffff', fontSize: 11, fontWeight: 600 }}>{launchingSoonText}</Box>
      </Box>
    )}
  </Box>
);
